import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { BuildPriceEntities } from 'client/data/models/build-price';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { Checkbox } from 'site-modules/shared/components/checkbox/checkbox';

export function OptionSection({
  features,
  title,
  optionIds,
  includedItems,
  requiredItems,
  trackingIdSuffix,
  onOptionUpdate,
}) {
  const onOptionChange = useCallback(
    ({ target: { value } }) => {
      onOptionUpdate(value);
    },
    [onOptionUpdate]
  );

  return (
    <ul className="option-section list-unstyled text-gray-darker px-0_25" aria-label={title}>
      {features.map(({ id, oemName, description, price }) => {
        const priceId = `option-price-${id}`;
        const descriptionId = `option-description-${id}`;
        const isIncluded = includedItems.includes(id);
        const isRequired = requiredItems.includes(id);
        const isSelected = optionIds.includes(id);
        const isChecked = isSelected || isRequired || isIncluded;

        return (
          <li key={`option-${id}`} className="mb-1">
            <div
              className={classnames('option-row card px-1 border-0', {
                included: isIncluded,
                required: isRequired,
                checked: isChecked,
              })}
            >
              <div className="d-flex justify-content-between align-items-center size-16 fw-medium">
                <Checkbox
                  id={id}
                  value={id}
                  tabIndex={isIncluded || isRequired ? -1 : undefined}
                  containerClassName="w-100"
                  checked={isChecked}
                  onChange={onOptionChange}
                  labelText={
                    <Fragment>
                      <span>{oemName}</span>
                      <span className="price ps-1 ps-lg-2" id={priceId}>
                        {isIncluded ? 'Included' : formatPriceString(get(price, 'baseMSRP', 0))}
                      </span>
                    </Fragment>
                  }
                  labelClassName="w-100 d-flex align-items-center"
                  customLabelClassName="center-y"
                  textLabelClassName="w-100 d-flex justify-content-between py-0_75 ms-2"
                  data-tracking-id={`build_price_select_${trackingIdSuffix}`}
                  data-tracking-value={oemName}
                  aria-describedby={`${priceId}${description ? ` ${descriptionId}` : ''}`}
                />
              </div>
              {description && (
                <Fragment>
                  <hr className="w-100 my-0" />
                  <ul className="description-list small px-3 pt-0_75 pb-0_25" id={descriptionId}>
                    {description.split(';').map((descriptionItem, index) => {
                      const key = `${oemName}-item-${index}`;
                      return (
                        <li key={key} className="mb-0_5">
                          {descriptionItem}
                        </li>
                      );
                    })}
                  </ul>
                </Fragment>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

OptionSection.propTypes = {
  features: PropTypes.arrayOf(BuildPriceEntities.Option).isRequired,
  optionIds: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  includedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  requiredItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  onOptionUpdate: PropTypes.func.isRequired,
  trackingIdSuffix: PropTypes.string.isRequired,
};
